import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { format } from 'date-fns';
import { getProjectDate, ProjectApiError } from '../../services/projectApi';

interface ProjectDateControlsProps {
  projectId: number;
  initialProjectDate?: string | null;
  onDateChange?: (newDate: string) => void;
}

export const ProjectDateControls: React.FC<ProjectDateControlsProps> = ({
  projectId,
  initialProjectDate,
  onDateChange,
}) => {
  const [projectDate, setProjectDate] = useState<string | null>(initialProjectDate || null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!initialProjectDate);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialProjectDate) {
      setProjectDate(initialProjectDate);
    }
  }, [initialProjectDate]);

  useEffect(() => {
    if (!projectId || initialProjectDate) return;

    const loadProjectDate = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getProjectDate(projectId);
        setProjectDate(response.projectDate);
        setTimezone(response.timezone || null);
        if (onDateChange) {
          onDateChange(response.projectDate);
        }
      } catch (err) {
        const apiError = err as ProjectApiError;
        setError(apiError.message || 'Failed to load project date');
        console.error('Error loading project date:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjectDate();
  }, [projectId, initialProjectDate, onDateChange]);

  const formatTimezone = (tz: string | null): string => {
    if (!tz) return '';
    try {
      const date = projectDate ? new Date(projectDate) : new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(date);
      const tzName = parts.find(part => part.type === 'timeZoneName')?.value || '';
      return tzName ? ` (${tzName})` : ` (${tz})`;
    } catch {
      return ` (${tz})`;
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Not set';
    try {
      const dateOnly = dateString.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        const date = new Date(dateString);
        return format(date, 'MMMM dd, yyyy');
      }
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthName = monthNames[month - 1];
      return `${monthName} ${day}, ${year}`;
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">Loading project date...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <CalendarToday fontSize="small" color="action" />
          <Typography variant="body1" component="div">
            <strong>Project date:</strong> {formatDate(projectDate)}
            {timezone && formatTimezone(timezone)}
          </Typography>
        </Box>

        {timezone && (
          <Typography variant="body2" color="text.secondary">
            Reminders use 8:00 AM{formatTimezone(timezone)}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
